import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  NotificationPreferenceUpdate,
  NotificationPreferencesView,
  NotificationSeverity,
  NotificationView,
} from '@asyagrup/group-hub-contracts';
import { api } from '@/lib/api/client';

/**
 * Re-exported, not re-declared.
 *
 * `NotificationView` and `NotificationSeverity` used to be written out again in
 * this file, by hand, next to a server interface with the same fields. Two
 * copies of one shape drift the moment either side gains a field, and the
 * failing direction is silent: the extra field simply never renders. The
 * catalogue that came with them (`packages/contracts/src/dto/notification.ts`)
 * is now the one place either side reads.
 */
export type { NotificationSeverity, NotificationView, NotificationPreferencesView };

/**
 * Two roots, not one, and the split is load-bearing.
 *
 * Everything under `all` is this user's inbox state — the badge and the list.
 * Mark-read makes both wrong at the same instant, so one prefix invalidation
 * that refetches both is exactly the mechanism we want, and new inbox queries
 * belong under it.
 *
 * The VAPID public key is not inbox state. It is a deployment constant: ADR
 * 0005 records that rotating the pair silently invalidates every subscription
 * already stored on a phone, so it is generated once and then left alone. That
 * is what the one-hour `staleTime` on `useVapidPublicKey` is claiming.
 *
 * It used to sit at `['notifications', 'push', 'public-key']`, which made it a
 * prefix-child of `all` — and `invalidateQueries` matches by key PREFIX, then
 * refetches every matching ACTIVE query outright; `staleTime` is never
 * consulted on that path. Both screens that mount `PushCard` also fire
 * mark-read, so opening a notification spent a request re-reading a constant.
 * With the card unmounted it was worse rather than better: the query was still
 * flagged invalidated, and that flag outranks `staleTime` from then on, so the
 * next mount refetched too.
 *
 * A separate root is the version of this that cannot rot. No notification
 * mutation can reach the key by prefix, whatever gets added above it later.
 */
export const notificationKeys = {
  all: ['notifications'] as const,
  unread: ['notifications', 'unread-count'] as const,
  /**
   * The filter is PART of the key.
   *
   * Without it, changing a filter would show the previous filter's rows until
   * the refetch landed — and react-query would keep serving the stale set to
   * whichever component mounted first. The bell mounts the UNFILTERED list, so
   * it also keeps its own cache entry rather than inheriting whatever the page
   * was last filtered to.
   */
  list: (filter: NotificationFilter = {}) => ['notifications', 'list', filter] as const,
  preferences: ['notifications', 'preferences'] as const,
};

/** Deliberately outside `notificationKeys`, for the reason above. Nothing
 *  invalidates this: the server's key pair outlives every session. */
export const pushKeys = {
  publicKey: ['push', 'public-key'] as const,
};

/**
 * Polling, not websockets. Realtime that silently dies is worse than no
 * realtime; a 60-second poll is boring and always works.
 *
 * Web Push is the phone-side answer and is layered on top (see `push.ts`) —
 * this stays as the fallback for anyone who declined the permission, and as the
 * thing that keeps the badge honest while the tab is open.
 *
 * Shared by the header bell and the bottom tab bar; react-query dedupes on the
 * key, so two callers are still one request.
 */
export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unread,
    queryFn: () => api.get<{ count: number }>('/notifications/unread-count'),
    refetchInterval: 60_000,
    // A phone spends most of its life with the screen off; polling a hidden tab
    // spends battery to learn nothing. Push covers that window.
    refetchIntervalInBackground: false,
  });
}

export interface NotificationFilter {
  /** A full code (`leave.decision`) or a group prefix (`leave`). */
  code?: string;
  severity?: NotificationSeverity;
  /** `YYYY-AA-GG`, both ends inclusive. */
  from?: string;
  to?: string;
  unread?: boolean;
}

/** Matches the server default, and the bell's page size. */
export const NOTIFICATION_PAGE_SIZE = 50;

function qs(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  // Empty is ABSENT, not empty-string: `severity=` is not "no filter", it is
  // asking for the empty string, and `z.enum` answers that with a 400.
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') search.set(key, String(value));
  }
  return search.toString();
}

/**
 * The inbox, paged by cursor.
 *
 * `getNextPageParam` returns the LAST row's cursor, and `undefined` — meaning
 * "no more" — as soon as a page comes back short. A full page is not proof
 * there is another one, so the last press of "daha fazla" fetches an empty page
 * and stops; that is one wasted request per session, against a `total` this
 * endpoint deliberately does not compute (a COUNT over the table this release
 * exists to keep small).
 */
export function useNotifications(enabled = true, filter: NotificationFilter = {}) {
  return useInfiniteQuery({
    queryKey: notificationKeys.list(filter),
    enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => {
      const query = qs({
        limit: NOTIFICATION_PAGE_SIZE,
        cursor: pageParam,
        code: filter.code,
        severity: filter.severity,
        from: filter.from,
        to: filter.to,
        unread: filter.unread === true ? 'true' : undefined,
      });
      return api.get<NotificationView[]>(`/notifications?${query}`, signal);
    },
    getNextPageParam: (last) =>
      last.length < NOTIFICATION_PAGE_SIZE ? undefined : last[last.length - 1]?.cursor,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notificationId?: string) =>
      api.post('/notifications/mark-read', { notificationId: notificationId ?? null }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

/**
 * Takes a row out of the inbox. `undefined` archives every READ row.
 *
 * Invalidates the same prefix mark-read does, because archiving changes the
 * badge too: an archived row that was unread is stamped read on the server, and
 * a badge that keeps counting a row no list shows is a counter with no way to
 * clear it.
 */
export function useArchive() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (notificationId?: string) =>
      api.post('/notifications/archive', { notificationId: notificationId ?? null }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: notificationKeys.all }),
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: notificationKeys.preferences,
    queryFn: () => api.get<NotificationPreferencesView>('/notifications/preferences'),
  });
}

/**
 * PUT rather than POST: one switch, one idempotent write. A double tap on a
 * phone is two requests in the same tenth of a second and both must land on the
 * same row with the same result.
 */
export function useSetNotificationPreference() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: NotificationPreferenceUpdate) =>
      api.put('/notifications/preferences', input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: notificationKeys.preferences }),
  });
}

/** `null` means the server has no VAPID keys configured — push is simply off,
 *  which is a deployment state, not an error to show anybody. */
export function useVapidPublicKey() {
  return useQuery({
    queryKey: pushKeys.publicKey,
    queryFn: () => api.get<{ publicKey: string | null }>('/notifications/push/public-key'),
    staleTime: 60 * 60_000,
    retry: false,
  });
}

export interface PushSubscribeInput {
  endpoint: string;
  p256dh: string;
  auth: string;
  userAgent?: string;
}

export function subscribeToPush(input: PushSubscribeInput): Promise<void> {
  return api.post('/notifications/push/subscribe', input);
}

export function unsubscribeFromPush(endpoint: string): Promise<void> {
  return api.post('/notifications/push/unsubscribe', { endpoint });
}
